import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { MapPin, Users, Clock, ClipboardCheck, Euro, CalendarDays, Sparkles, ArrowRight } from "lucide-react";
import { MAP_H, DISTRITOS_MAPA, ILHAS_MAPA } from "@/data/mapa-distritos";
import { DISTRITOS_INFO } from "@/data/distritos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback estático (usado enquanto o fetch está em curso ou se falhar)
const DEFAULT_CTAS = [
  { id: "diagnostico", icon: "ClipboardCheck", label: "Diagnóstico Legal", event: "open-diagnostico", order: 0 },
  { id: "orcamento", icon: "Euro", label: "Orçamento Imediato", event: "open-orcamento", order: 1 },
  { id: "agendamento", icon: "CalendarDays", label: "Agendar Inspeção", event: "open-agendamento", order: 2 },
  { id: "assistente", icon: "Sparkles", label: "Assistente IA", event: "open-chat", order: 3 },
];
const DEFAULT_QUESTION = "O que precisa em {distrito}?";

export default function CoberturaNacional() {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [tip, setTip] = useState({ x: 0, y: 0 });
  const wrapRef = useRef(null);
  const info = selected ? DISTRITOS_INFO[selected.id] : null;

  // Fetch dos CTAs configurados no admin
  const [ctas, setCtas] = useState(DEFAULT_CTAS);
  const [question, setQuestion] = useState(DEFAULT_QUESTION);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/cobertura-settings`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (Array.isArray(data.ctas) && data.ctas.length > 0) {
          setCtas(data.ctas.slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
        }
        if (data.question_template) setQuestion(data.question_template);
      } catch (_e) {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onMove = (e) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setTip({ x: e.clientX - r.left, y: e.clientY - r.top });
  };

  return (
    <section className="py-14 bg-[#0E1A2B] text-white" data-testid="cobertura-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.24em] uppercase font-semibold text-[#4FC6D8] mb-4">
              Cobertura Nacional
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight font-medium leading-[1.1]">
              Técnicos em todo
              <br />o território.
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed max-w-md">
              Consulte o distrito no mapa para identificar a equipa técnica INXPEK responsável pela sua área geográfica.
            </p>

            <div className="mt-8 min-h-[280px]">
              <AnimatePresence mode="wait">
                {selected ? (
                  <motion.div
                    key={selected.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className="border border-white/15 rounded-lg p-6"
                    data-testid="cobertura-panel"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MapPin size={16} className="text-[#4FC6D8]" />
                      <h3 className="font-heading text-xl font-semibold" data-testid="cobertura-distrito-nome">
                        {selected.name}
                      </h3>
                    </div>
                    <p className="text-xs text-white/50 mb-3">
                      {(question || DEFAULT_QUESTION).replace("{distrito}", selected.name)}
                    </p>
                    <div className="grid grid-cols-2 gap-2.5">
                      {ctas.map((c) => {
                        const IconComp = Icons[c.icon] || Icons.Layers;
                        return (
                          <button
                            key={c.id}
                            data-testid={`cobertura-cta-${c.id}`}
                            onClick={() => window.dispatchEvent(new Event(c.event))}
                            className="group flex items-center gap-2.5 border border-white/15 rounded-lg px-3.5 min-h-[56px] text-left text-sm font-medium hover:border-[#4FC6D8] hover:bg-[#4FC6D8]/10 active:scale-[0.98] transition-all"
                          >
                            <IconComp size={16} className="text-[#4FC6D8] shrink-0" />
                            <span className="leading-tight">{c.label}</span>
                            <ArrowRight size={13} className="ml-auto opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border border-dashed border-white/20 rounded-lg p-8 text-center"
                    data-testid="cobertura-empty"
                  >
                    <MapPin size={26} className="mx-auto text-[#4FC6D8] mb-3" />
                    <p className="text-sm text-white/60 leading-relaxed">
                      Toque num distrito do mapa
                      <br />para conhecer a equipa local.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col items-center">
            <div ref={wrapRef} onMouseMove={onMove} className="relative w-full max-w-[380px] lg:max-w-[440px]">
              <svg
                viewBox={`-124 0 448 ${MAP_H}`}
                className="w-full"
                role="group"
                aria-label="Mapa de Portugal por distritos e regiões autónomas"
                data-testid="mapa-portugal"
              >
              {DISTRITOS_MAPA.map((d) => {
                const active = selected?.id === d.id;
                const hover = hovered === d.id;
                return (
                  <path
                    key={d.id}
                    d={d.d}
                    data-testid={`mapa-distrito-${d.id}`}
                    onClick={() => setSelected(d)}
                    onMouseEnter={() => setHovered(d.id)}
                    onMouseLeave={() => setHovered(null)}
                    fill={active ? "#1698AC" : hover ? "rgba(79,198,216,0.45)" : "rgba(255,255,255,0.10)"}
                    stroke="#0E1A2B"
                    strokeWidth="1.4"
                    className="cursor-pointer transition-[fill] duration-200"
                  />
                );
              })}
              {ILHAS_MAPA.map((il) => {
                const active = selected?.id === il.id;
                const hover = hovered === il.id;
                return (
                  <g
                    key={il.id}
                    data-testid={`mapa-ilhas-${il.id}`}
                    onClick={() => setSelected(il)}
                    onMouseEnter={() => setHovered(il.id)}
                    onMouseLeave={() => setHovered(null)}
                    className="cursor-pointer"
                  >
                    <rect
                      x={il.box.x}
                      y={il.box.y}
                      width={il.box.w}
                      height={il.box.h}
                      rx="6"
                      fill={active ? "rgba(22,152,172,0.14)" : hover ? "rgba(79,198,216,0.08)" : "rgba(255,255,255,0.02)"}
                      stroke={active ? "#1698AC" : hover ? "rgba(79,198,216,0.7)" : "rgba(255,255,255,0.22)"}
                      strokeWidth="1"
                      strokeDasharray="4 3"
                      className="transition-[fill,stroke] duration-200"
                    />
                    <path
                      d={il.d}
                      pointerEvents="none"
                      fill={active ? "#1698AC" : hover ? "rgba(79,198,216,0.75)" : "rgba(255,255,255,0.38)"}
                      stroke={active ? "#4FC6D8" : "rgba(255,255,255,0.25)"}
                      strokeWidth="0.6"
                      className="transition-[fill] duration-200"
                    />
                    <text
                      x={il.box.x + 8}
                      y={il.box.y + il.box.h - 8}
                      pointerEvents="none"
                      fontSize="9"
                      letterSpacing="1.5"
                      className="uppercase"
                      fill={active || hover ? "#4FC6D8" : "rgba(255,255,255,0.45)"}
                    >
                      {il.name}
                    </text>
                  </g>
                );
              })}
              {selected && (
                <g pointerEvents="none">
                  <circle cx={selected.cx} cy={selected.cy} r="7" fill="#4FC6D8" opacity="0.35">
                    <animate attributeName="r" values="6;11;6" dur="1.8s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={selected.cx} cy={selected.cy} r="3.2" fill="#4FC6D8" />
                </g>
              )}
            </svg>
              {hovered && (
                <div
                  data-testid="mapa-tooltip"
                  className="pointer-events-none absolute z-10 px-3 py-1.5 rounded-md bg-white text-[#0E1A2B] text-xs font-semibold shadow-lg whitespace-nowrap"
                  style={{ left: tip.x + 14, top: tip.y - 10 }}
                >
                  {[...DISTRITOS_MAPA, ...ILHAS_MAPA].find((d) => d.id === hovered)?.name}
                </div>
              )}
            </div>
            <p className="mt-3 text-[11px] text-white/40">Portugal continental · Açores · Madeira</p>
          </div>
        </div>
      </div>
    </section>
  );
}
